import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Toggle from '@amzn/meridian/toggle';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { adminApi, BulkImportSummary } from '../service/adminApiService';
import { CSVDropzone } from '../components/CSVDropzone';
import { useToast } from '../components/ToastProvider';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';

export const BulkImportPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [filename, setFilename] = useState('');
  const [csvText, setCsvText] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkImportSummary | null>(null);
  const [dryRunPassed, setDryRunPassed] = useState(false);

  const submit = async () => {
    if (!csvText.trim()) {
      toast.push({ type: 'error', message: 'Select a CSV file before submitting.' });
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const response = await adminApi.bulkImportUsers({ csv: csvText, dry_run: dryRun });
      setResult(response);
      if (!dryRun) {
        toast.push({
          type: 'success',
          message: `Imported ${response.successful_rows} user(s)`,
        });
      } else {
        if (response.failed_rows === 0) {
          setDryRunPassed(true);
          toast.push({
            type: 'success',
            message: `Dry run succeeded: ${response.total_rows} row(s) ready to import`,
          });
        } else {
          setDryRunPassed(false);
          toast.push({
            type: 'error',
            message: `Dry run found ${response.failed_rows} error(s)`,
          });
        }
      }
    } catch (err: any) {
      const payload = err?.details as BulkImportSummary | undefined;
      if (payload && typeof payload === 'object') {
        setResult(payload);
      }
      toast.push({ type: 'error', message: err?.message || 'Bulk import failed' });
    } finally {
      setSubmitting(false);
    }
  };

  const applyAfterDryRun = async () => {
    // Intentionally don't mutate ``dryRun`` here — pass ``dry_run: false``
    // directly to the API. Previously we called ``setDryRun(false)``
    // before the request, which meant that if the import failed, the
    // page was left with the toggle showing "Import" and a subsequent
    // click would perform a real write without the user realising the
    // safety toggle had flipped.
    setSubmitting(true);
    try {
      const response = await adminApi.bulkImportUsers({ csv: csvText, dry_run: false });
      setResult(response);
      toast.push({
        type: 'success',
        message: `Imported ${response.successful_rows} user(s)`,
      });
      navigate('/admin/users');
    } catch (err: any) {
      toast.push({ type: 'error', message: err?.message || 'Bulk import failed' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Column spacing="large">
      <PageHeader
        title="Bulk import users"
        subtitle="Upload a CSV of user-to-group mappings. Dry run first to validate."
      />

      <Section>
      <Column spacing="medium" maxWidth={900}>
      <Alert type="neutral" title="CSV format">
        Two columns: <code>user_alias,posix_groups</code>. Separate multiple groups with a
        semicolon. Header row is required. Max 500 rows.
      </Alert>

      <CSVDropzone
        onFileSelected={(name, contents) => {
          setFilename(name);
          setCsvText(contents);
          setResult(null);
          setDryRunPassed(false);
        }}
      />
      {filename && (
        <Text type="b200" color="secondary">
          Loaded: {filename}
        </Text>
      )}

      <Row alignmentVertical="center" spacing="medium">
        <Toggle checked={dryRun} onChange={setDryRun}>
          Dry run (validate only)
        </Toggle>
        <Text type="b200" color="secondary">
          Leave on first to preview results without touching the database.
        </Text>
      </Row>

      <Row alignmentHorizontal="end" spacing="small">
        <Button type="tertiary" onClick={() => navigate('/admin/users')}>
          Cancel
        </Button>
        <Button type="primary" onClick={submit} disabled={submitting || !csvText.trim()}>
          {submitting ? 'Submitting…' : dryRun ? 'Run dry run' : 'Import'}
        </Button>
        {dryRunPassed && dryRun && (
          <Button type="secondary" onClick={applyAfterDryRun} disabled={submitting}>
            Apply ({result?.total_rows} rows)
          </Button>
        )}
      </Row>

      {result && (
        <Column spacing="small">
          <Heading level={4}>Results</Heading>
          <Text>
            Total: {result.total_rows} · Succeeded: {result.successful_rows} · Failed:{' '}
            {result.failed_rows}
            {result.dry_run ? ' (dry run — no writes)' : ''}
          </Text>
          {result.row_errors && result.row_errors.length > 0 && (
            <Table headerRows={1} showDividers={true} aria-label="Row errors">
              <TableRow>
                <TableCell>Row</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
              {result.row_errors.map((re) => (
                <TableRow key={re.row_number}>
                  <TableCell>{re.row_number}</TableCell>
                  <TableCell>{re.error}</TableCell>
                </TableRow>
              ))}
            </Table>
          )}
          {result.rows && result.rows.length > 0 && (
            <Table headerRows={1} showDividers={true} aria-label="Parsed rows">
              <TableRow>
                <TableCell>user_alias</TableCell>
                <TableCell>posix_groups</TableCell>
              </TableRow>
              {result.rows.slice(0, 50).map((row) => (
                <TableRow key={row.user_alias}>
                  <TableCell>{row.user_alias}</TableCell>
                  <TableCell>{row.posix_groups.join('; ')}</TableCell>
                </TableRow>
              ))}
            </Table>
          )}
        </Column>
      )}
      </Column>
      </Section>
    </Column>
  );
};
